import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';


const fmt = value => value ? new Date(value).toLocaleString() : 'Not recorded';
const size = value => {
  const bytes = Number(value || 0);
  if (!bytes) return 'Unknown size';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const incidentTitle = incident => incident?.filename || (
  incident?.operation === 'invitation_email_delivery' ? 'Invitation email delivery' : 'Operational incident'
);

const statusStyle = {
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-700',
  dismissed: 'bg-gray-100 text-gray-600',
};

export default function SupportIncidentPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [reference, setReference] = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState('');
  const [draft, setDraft] = useState({ status: 'open', assigned_to: '', note: '', resolution_summary: '' });

  const selectIncident = incident => {
    setSelected(incident);
    setDraft({
      status: incident.status || 'open',
      assigned_to: incident.assigned_to || '',
      note: '',
      resolution_summary: incident.resolution_summary || '',
    });
  };

  const loadRecent = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/platform/operations/support-incidents?limit=100');
      setItems(data.items || []);
      setCounts(data.counts || {});
      if (selected) {
        const refreshed = (data.items || []).find(item => item.reference === selected.reference);
        if (refreshed) selectIncident(refreshed);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load support references');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRecent(); }, []);

  const findReference = async event => {
    event.preventDefault();
    const value = reference.trim().toUpperCase();
    if (!value) return toast.error('Enter the reference supplied to the user');
    setNotFound('');
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/platform/operations/support-incidents?reference=${encodeURIComponent(value)}&limit=1`);
      setCounts(data.counts || {});
      if (!data.items?.length) {
        setSelected(null);
        setNotFound(`${value} is not in the operational incident register. References issued before this register was introduced may still exist only in deployment logs.`);
        return;
      }
      selectIncident(data.items[0]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not search this support reference');
    } finally {
      setLoading(false);
    }
  };

  const save = async event => {
    event.preventDefault();
    if (!selected) return;
    try {
      const { data } = await axiosInstance.patch(
        `/platform/operations/support-incidents/${encodeURIComponent(selected.reference)}`,
        draft,
      );
      selectIncident(data);
      setItems(current => current.map(item => item.reference === data.reference ? data : item));
      toast.success(`${data.reference} updated`);
      loadRecent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update this support case');
    }
  };

  return <div className="mt-6 space-y-6" data-testid="support-reference-operations">
    <section className="rounded-xl border border-indigo-100 bg-white p-5">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 text-indigo-600" size={21}/>
        <div><h2 className="font-semibold text-gray-950">Support-reference troubleshooting</h2><p className="mt-1 text-sm text-gray-500">Enter the reference shown to a user. Qflow links it to the organization, user, operation stage and safe technical cause without storing document contents, passwords or API keys.</p></div>
      </div>
      <form onSubmit={findReference} className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input value={reference} onChange={event=>setReference(event.target.value.toUpperCase())} placeholder="e.g. UPL-A33E8203" aria-label="Support reference" className="min-w-0 flex-1 rounded-lg border px-3 py-2.5 font-mono text-sm uppercase"/>
        <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white"><Search size={16}/>Find reference</button>
        <button type="button" onClick={loadRecent} className="inline-flex items-center justify-center gap-2 rounded-lg border bg-white px-4 py-2.5 text-sm"><RefreshCw size={15}/>Refresh</button>
      </form>
      {notFound && <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{notFound}</p>}
    </section>

    <div className="grid gap-4 sm:grid-cols-4">
      {['open','investigating','resolved','dismissed'].map(value=><div key={value} className="rounded-xl border bg-white p-4"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">{value}</p><p className="mt-1 text-2xl font-semibold">{counts[value] || 0}</p></div>)}
    </div>

    <div className="grid gap-6 lg:grid-cols-[minmax(320px,.8fr)_minmax(0,1.2fr)]">
      <section className="overflow-hidden rounded-xl border bg-white">
        <div className="border-b px-5 py-4"><h2 className="font-semibold">Recent references</h2><p className="text-sm text-gray-500">Newest incidents across all organizations.</p></div>
        <div className="max-h-[640px] divide-y overflow-auto">
          {loading ? <p className="p-8 text-center text-sm text-gray-500">Loading support references…</p> : items.length ? items.map(item=><button type="button" key={item.reference} onClick={()=>selectIncident(item)} className={`block w-full px-5 py-4 text-left hover:bg-indigo-50 ${selected?.reference===item.reference?'bg-indigo-50':''}`}><div className="flex items-center justify-between gap-3"><span className="font-mono text-sm font-semibold text-indigo-700">{item.reference}</span><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle[item.status] || statusStyle.open}`}>{item.status}</span></div><p className="mt-2 truncate text-sm font-medium text-gray-900">{incidentTitle(item)}</p><p className="mt-1 text-xs text-gray-500">{item.tenant_name} · {fmt(item.created_at)}</p></button>) : <p className="p-8 text-center text-sm text-gray-500">No operational incidents recorded yet.</p>}
        </div>
      </section>

      <section className="rounded-xl border bg-white p-5">
        {selected ? <form onSubmit={save}>
          <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="font-mono text-sm font-semibold text-indigo-700">{selected.reference}</p><h2 className="mt-1 text-xl font-semibold">{incidentTitle(selected)}</h2><p className="mt-1 text-sm text-gray-500">{selected.tenant_name} · {selected.user_name || selected.user_email || 'Unknown user'}</p></div><span className={`rounded-full px-3 py-1 text-xs font-medium ${statusStyle[selected.status] || statusStyle.open}`}>{selected.status}</span></div>
          <dl className="mt-5 grid gap-3 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-2"><div><dt className="text-xs text-gray-500">Occurred</dt><dd>{fmt(selected.created_at)}</dd></div><div><dt className="text-xs text-gray-500">{selected.operation === 'invitation_email_delivery' ? 'Recipient domain' : 'File size'}</dt><dd>{selected.operation === 'invitation_email_delivery' ? selected.recipient_domain || 'Not recorded' : size(selected.file_size_bytes)}</dd></div><div><dt className="text-xs text-gray-500">Operation stage</dt><dd>{(selected.stage || 'unknown').replaceAll('_',' ')}</dd></div><div><dt className="text-xs text-gray-500">Error type</dt><dd>{selected.error_type || 'Unknown'}</dd></div><div><dt className="text-xs text-gray-500">{selected.operation === 'invitation_email_delivery' ? 'Invitation ID' : 'Upload ID'}</dt><dd className="break-all font-mono text-xs">{selected.operation === 'invitation_email_delivery' ? selected.invitation_id || 'Not recorded' : selected.upload_id || 'Not recorded'}</dd></div><div><dt className="text-xs text-gray-500">Tenant ID</dt><dd className="font-mono text-xs">{selected.tenant_id}</dd></div></dl>
          <div className="mt-4 rounded-lg border border-red-100 bg-red-50 p-4"><div className="flex items-center gap-2 text-sm font-medium text-red-800"><AlertTriangle size={16}/>Technical cause</div><p className="mt-2 break-words font-mono text-xs leading-5 text-red-900">{selected.technical_summary || 'No technical summary was captured.'}</p></div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2"><label className="text-sm font-medium text-gray-700">Status<select value={draft.status} onChange={event=>setDraft(current=>({...current,status:event.target.value}))} className="mt-1 block w-full rounded-lg border bg-white px-3 py-2"><option value="open">Open</option><option value="investigating">Investigating</option><option value="resolved">Resolved</option><option value="dismissed">Dismissed</option></select></label><label className="text-sm font-medium text-gray-700">Assigned to<input value={draft.assigned_to} onChange={event=>setDraft(current=>({...current,assigned_to:event.target.value}))} placeholder="Name or team" className="mt-1 block w-full rounded-lg border px-3 py-2"/></label></div>
          <label className="mt-4 block text-sm font-medium text-gray-700">Resolution summary<textarea value={draft.resolution_summary} onChange={event=>setDraft(current=>({...current,resolution_summary:event.target.value}))} rows="3" placeholder="What fixed the issue or what should the user do next?" className="mt-1 block w-full rounded-lg border px-3 py-2"/></label>
          <label className="mt-4 block text-sm font-medium text-gray-700">Add internal note<textarea value={draft.note} onChange={event=>setDraft(current=>({...current,note:event.target.value}))} rows="2" placeholder="Investigation note (visible only to UI3)" className="mt-1 block w-full rounded-lg border px-3 py-2"/></label>
          {selected.notes?.length ? <div className="mt-4 rounded-lg border p-4"><p className="text-sm font-medium">Case notes</p><div className="mt-2 space-y-2">{selected.notes.map((note,index)=><div key={`${note.created_at}-${index}`} className="text-sm"><p>{note.text}</p><p className="text-xs text-gray-400">{note.created_by_name || 'UI3'} · {fmt(note.created_at)}</p></div>)}</div></div> : null}
          <button className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white"><CheckCircle2 size={16}/>Save support case</button>
        </form> : <div className="flex min-h-[360px] items-center justify-center text-center"><div><Search className="mx-auto text-gray-300" size={34}/><p className="mt-3 font-medium text-gray-700">Select a recent incident or search a reference</p><p className="mt-1 text-sm text-gray-500">The diagnostic detail will appear here.</p></div></div>}
      </section>
    </div>
  </div>;
}
